import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Loader2,
  Search,
  Send,
  Ticket,
  Trash2,
  Clock,
  Users,
  Gift,
  RefreshCw,
  Plus,
  Copy,
  Mail,
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

import CustomerPeek from "@/components/admin/CustomerPeek";

const gbpDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("en-GB", { year: "numeric", month: "short", day: "numeric" });
  } catch {
    return iso.slice(0, 10);
  }
};

// ─────── Summary widget ───────

const SummaryStat = ({ label, value, accent = false, hint = null, testid }) => (
  <div
    className="bg-white border border-[#DDD1BE] rounded-2xl p-4 flex flex-col"
    data-testid={testid}
  >
    <p className="text-[10px] uppercase tracking-[0.22em] text-[#6A5F58]">{label}</p>
    <p className={`mt-2 text-3xl font-serif ${accent ? "text-[#B76E79]" : "text-[#0F0E0E]"}`}>{value ?? 0}</p>
    {hint && <p className="mt-1 text-[11px] text-[#6A5F58]">{hint}</p>}
  </div>
);

// ─────── Manual voucher issuer ───────

const ManualIssuer = ({ onIssued }) => {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    email: "",
    unit_scope: "touring_any",
    unit_id: "",
    expires_in_days: 365,
    note: "",
  });
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!form.email) { toast.error("Enter a customer email."); return; }
    if (form.unit_scope === "lodge" && !form.unit_id) { toast.error("Pick a lodge."); return; }
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/admin/loyalty/vouchers/manual`, form);
      toast.success(`Issued voucher ${data.code}`);
      setForm({ email: "", unit_scope: "touring_any", unit_id: "", expires_in_days: 365, note: "" });
      setOpen(false);
      onIssued?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not issue voucher.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="bg-white border border-[#DDD1BE] rounded-2xl p-4 md:p-5" data-testid="manual-issuer">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Plus size={16} className="text-[#B76E79]" strokeWidth={1.7} />
          <p className="font-serif text-lg text-[#14100F]">Issue a voucher manually</p>
        </div>
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          className="text-xs uppercase tracking-[0.14em] text-[#6A5F58] hover:text-[#0F0E0E]"
          data-testid="manual-issuer-toggle"
        >
          {open ? "Hide" : "Show form"}
        </button>
      </div>
      {open && (
        <div className="mt-4 grid grid-cols-1 md:grid-cols-6 gap-3">
          <div className="md:col-span-2">
            <Label className="text-[11px] uppercase tracking-[0.16em] text-[#6A5F58]">Customer email</Label>
            <Input
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              className="mt-1"
              placeholder="guest@example.com"
              data-testid="manual-email"
            />
          </div>
          <div>
            <Label className="text-[11px] uppercase tracking-[0.16em] text-[#6A5F58]">Scope</Label>
            <Select value={form.unit_scope} onValueChange={(v) => setForm({ ...form, unit_scope: v, unit_id: "" })}>
              <SelectTrigger className="mt-1" data-testid="manual-scope"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="touring_any">Touring (any pitch)</SelectItem>
                <SelectItem value="lodge">Lodge (specific)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {form.unit_scope === "lodge" && (
            <div>
              <Label className="text-[11px] uppercase tracking-[0.16em] text-[#6A5F58]">Lodge</Label>
              <Select value={form.unit_id} onValueChange={(v) => setForm({ ...form, unit_id: v })}>
                <SelectTrigger className="mt-1" data-testid="manual-lodge"><SelectValue placeholder="Pick lodge" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="lodge-heron">Heron</SelectItem>
                  <SelectItem value="lodge-kingfisher">Kingfisher</SelectItem>
                  <SelectItem value="lodge-alpine">Alpine</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}
          <div>
            <Label className="text-[11px] uppercase tracking-[0.16em] text-[#6A5F58]">Valid days</Label>
            <Input
              type="number"
              min={1}
              max={1095}
              value={form.expires_in_days}
              onChange={(e) => setForm({ ...form, expires_in_days: Number(e.target.value) })}
              className="mt-1"
              data-testid="manual-days"
            />
          </div>
          <div className="md:col-span-2">
            <Label className="text-[11px] uppercase tracking-[0.16em] text-[#6A5F58]">Note (optional)</Label>
            <Input
              value={form.note}
              onChange={(e) => setForm({ ...form, note: e.target.value })}
              className="mt-1"
              placeholder="Goodwill · complaint resolution · comp night"
              data-testid="manual-note"
            />
          </div>
          <div className="md:col-span-6 flex justify-end">
            <button
              type="button"
              disabled={busy}
              onClick={submit}
              data-testid="manual-issue-submit"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#0F0E0E] text-[#F5EFE6] hover:bg-[#B76E79] transition-colors text-xs uppercase tracking-[0.14em] disabled:opacity-60"
            >
              {busy ? <Loader2 size={13} className="animate-spin" /> : <Send size={13} />}
              Issue voucher
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

// ─────── Vouchers table ───────

const StatusPill = ({ v }) => {
  const now = new Date();
  const expires = v.expires_at ? new Date(v.expires_at) : null;
  if (v.redeemed_at && v.redeemed_reason === "voided-by-admin") {
    return <span className="px-2 py-0.5 rounded-full text-[10px] uppercase tracking-[0.16em] bg-[#3A322E] text-[#F5EFE6]">Voided</span>;
  }
  if (v.redeemed_at) {
    return <span className="px-2 py-0.5 rounded-full text-[10px] uppercase tracking-[0.16em] bg-[#0F0E0E] text-[#F5EFE6]">Redeemed</span>;
  }
  if (expires && expires < now) {
    return <span className="px-2 py-0.5 rounded-full text-[10px] uppercase tracking-[0.16em] bg-[#B76E79] text-white">Expired</span>;
  }
  if (v.reserved_booking_id) {
    return <span className="px-2 py-0.5 rounded-full text-[10px] uppercase tracking-[0.16em] bg-[#E4B2A8] text-[#0F0E0E]">Reserved</span>;
  }
  return <span className="px-2 py-0.5 rounded-full text-[10px] uppercase tracking-[0.16em] bg-[#C9A47E] text-[#0F0E0E]">Active</span>;
};

const VouchersTable = ({ vouchers, onRefresh, onPeek }) => {
  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState("all");

  const filtered = useMemo(() => {
    const now = new Date();
    const q = query.trim().toLowerCase();
    return (vouchers || []).filter((v) => {
      // Status filter
      const isVoided = v.redeemed_reason === "voided-by-admin";
      const isExpired = v.expires_at && new Date(v.expires_at) < now && !v.redeemed_at;
      const isRedeemed = !!v.redeemed_at && !isVoided;
      const isActive = !v.redeemed_at && !isExpired;
      if (filter === "active" && !isActive) return false;
      if (filter === "redeemed" && !isRedeemed) return false;
      if (filter === "expired" && !isExpired) return false;
      if (filter === "voided" && !isVoided) return false;
      if (!q) return true;
      return (v.code || "").toLowerCase().includes(q) || (v.email || "").toLowerCase().includes(q);
    });
  }, [vouchers, query, filter]);

  const copyCode = async (code) => {
    try { await navigator.clipboard.writeText(code); toast.success("Copied."); } catch { toast.error("Copy failed."); }
  };

  const patch = async (code, body) => {
    try {
      await axios.patch(`${API}/admin/loyalty/vouchers/${code}`, body);
      toast.success(body.action === "void" ? "Voucher voided." : `Extended by ${body.months || 6} months.`);
      onRefresh?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not update.");
    }
  };

  return (
    <div className="bg-white border border-[#DDD1BE] rounded-2xl p-4 md:p-5" data-testid="vouchers-table">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2">
          <Ticket size={16} className="text-[#B76E79]" strokeWidth={1.7} />
          <p className="font-serif text-lg text-[#14100F]">Vouchers ({filtered.length})</p>
        </div>
        <div className="flex flex-wrap gap-2 items-center">
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#6A5F58]" />
            <Input
              className="pl-8 h-9 w-64"
              placeholder="Search code or email"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              data-testid="vouchers-search"
            />
          </div>
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger className="w-40 h-9" data-testid="vouchers-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              <SelectItem value="active">Active</SelectItem>
              <SelectItem value="redeemed">Redeemed</SelectItem>
              <SelectItem value="expired">Expired</SelectItem>
              <SelectItem value="voided">Voided</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="mt-4 overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-[10px] uppercase tracking-[0.18em] text-[#6A5F58] border-b border-[#DDD1BE]">
              <th className="py-2 pr-3">Code</th>
              <th className="py-2 pr-3">Owner</th>
              <th className="py-2 pr-3">Scope</th>
              <th className="py-2 pr-3">Earned</th>
              <th className="py-2 pr-3">Expires</th>
              <th className="py-2 pr-3">Status</th>
              <th className="py-2 pr-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && (
              <tr><td colSpan={7} className="py-6 text-center text-[#6A5F58] text-sm">No vouchers match.</td></tr>
            )}
            {filtered.map((v) => (
              <tr key={v.code} className="border-b border-[#F0EAD8] hover:bg-[#FBF7F1]" data-testid={`voucher-row-${v.code}`}>
                <td className="py-2 pr-3 font-mono font-semibold tracking-[0.08em] text-[#0F0E0E]">{v.code}</td>
                <td className="py-2 pr-3 text-[#3A322E]">
                  {v.email ? (
                    <button
                      type="button"
                      onClick={() => onPeek?.(v.email)}
                      className="text-left text-[#3A322E] hover:text-[#B76E79] underline decoration-dotted underline-offset-4"
                      title="Open customer story"
                      data-testid={`peek-voucher-${v.code}`}
                    >
                      {v.email}
                    </button>
                  ) : "—"}
                </td>
                <td className="py-2 pr-3 text-[#3A322E]">
                  {v.unit_scope === "lodge" ? `Lodge · ${v.unit_id || "—"}` : "Touring (any)"}
                </td>
                <td className="py-2 pr-3 text-[#6A5F58]">{gbpDate(v.earned_at)}</td>
                <td className="py-2 pr-3 text-[#6A5F58]">{gbpDate(v.expires_at)}</td>
                <td className="py-2 pr-3"><StatusPill v={v} /></td>
                <td className="py-2 pr-3 text-right whitespace-nowrap">
                  <button type="button" onClick={() => copyCode(v.code)} title="Copy" className="inline-flex items-center p-1.5 text-[#6A5F58] hover:text-[#0F0E0E]" data-testid={`voucher-copy-${v.code}`}>
                    <Copy size={14} />
                  </button>
                  {!v.redeemed_at && (
                    <>
                      <button type="button" onClick={() => patch(v.code, { action: "extend", months: 6 })} title="Extend +6mo" className="inline-flex items-center p-1.5 text-[#6A5F58] hover:text-[#0F0E0E]" data-testid={`voucher-extend-${v.code}`}>
                        <Clock size={14} />
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          if (window.confirm(`Void voucher ${v.code}? This cannot be undone.`)) patch(v.code, { action: "void" });
                        }}
                        title="Void"
                        className="inline-flex items-center p-1.5 text-[#B76E79] hover:text-[#8f4a52]"
                        data-testid={`voucher-void-${v.code}`}
                      >
                        <Trash2 size={14} />
                      </button>
                    </>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ─────── Ledger table ───────

const LedgerTable = ({ ledger, onPeek }) => {
  const [query, setQuery] = useState("");
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return (ledger || []).filter((l) => !q || (l.email || "").toLowerCase().includes(q));
  }, [ledger, query]);

  return (
    <div className="bg-white border border-[#DDD1BE] rounded-2xl p-4 md:p-5" data-testid="ledger-table">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2">
          <Gift size={16} className="text-[#B76E79]" strokeWidth={1.7} />
          <p className="font-serif text-lg text-[#14100F]">Loyalty ledger ({filtered.length})</p>
        </div>
        <div className="relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#6A5F58]" />
          <Input className="pl-8 h-9 w-64" placeholder="Search email" value={query} onChange={(e) => setQuery(e.target.value)} data-testid="ledger-search" />
        </div>
      </div>

      <div className="mt-4 overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-[10px] uppercase tracking-[0.18em] text-[#6A5F58] border-b border-[#DDD1BE]">
              <th className="py-2 pr-3">Customer</th>
              <th className="py-2 pr-3">Touring nights</th>
              <th className="py-2 pr-3">Touring vouchers</th>
              <th className="py-2 pr-3">Lodge progress</th>
              <th className="py-2 pr-3">Updated</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && (
              <tr><td colSpan={5} className="py-6 text-center text-[#6A5F58] text-sm">No customers with completed stays yet.</td></tr>
            )}
            {filtered.map((l) => (
              <tr key={l.email} className="border-b border-[#F0EAD8]" data-testid={`ledger-row-${l.email}`}>
                <td className="py-2 pr-3 text-[#3A322E]">
                  <button
                    type="button"
                    onClick={() => onPeek?.(l.email)}
                    className="text-left text-[#3A322E] hover:text-[#B76E79] underline decoration-dotted underline-offset-4"
                    title="Open customer story"
                    data-testid={`peek-ledger-${l.email}`}
                  >
                    {l.email}
                  </button>
                </td>
                <td className="py-2 pr-3 text-[#3A322E]">{l.touring_nights_total || 0}</td>
                <td className="py-2 pr-3 text-[#3A322E]">{l.touring_vouchers_issued || 0}</td>
                <td className="py-2 pr-3 text-[#3A322E]">
                  {Object.entries(l.lodge_progress || {}).length === 0 ? "—" : (
                    <div className="flex flex-wrap gap-1.5">
                      {Object.entries(l.lodge_progress || {}).map(([unit, p]) => (
                        <span key={unit} className="px-2 py-0.5 rounded-full bg-[#F5EFE6] border border-[#DDD1BE] text-[11px]">
                          {unit.replace("lodge-", "")}: {p.nights}n · {p.vouchers_issued}v
                        </span>
                      ))}
                    </div>
                  )}
                </td>
                <td className="py-2 pr-3 text-[#6A5F58]">{gbpDate(l.updated_at)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ─────── Referrals table ───────

const ReferralsTable = ({ referrals, onRefresh, onPeek }) => {
  const forceReward = async (bookingId) => {
    if (!window.confirm("Force-reward this referral now? This mints a bonus voucher for the referrer immediately.")) return;
    try {
      const { data } = await axios.post(`${API}/admin/loyalty/referrals/${bookingId}/force-reward`);
      toast.success(`Bonus voucher ${data.voucher_code} issued.`);
      onRefresh?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not reward.");
    }
  };

  return (
    <div className="bg-white border border-[#DDD1BE] rounded-2xl p-4 md:p-5" data-testid="referrals-table">
      <div className="flex items-center gap-2">
        <Users size={16} className="text-[#B76E79]" strokeWidth={1.7} />
        <p className="font-serif text-lg text-[#14100F]">Referrals ({(referrals || []).length})</p>
      </div>
      <div className="mt-4 overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-[10px] uppercase tracking-[0.18em] text-[#6A5F58] border-b border-[#DDD1BE]">
              <th className="py-2 pr-3">Referrer</th>
              <th className="py-2 pr-3">Friend</th>
              <th className="py-2 pr-3">Booking</th>
              <th className="py-2 pr-3">Discount</th>
              <th className="py-2 pr-3">Status</th>
              <th className="py-2 pr-3">Bonus voucher</th>
              <th className="py-2 pr-3">Created</th>
              <th className="py-2 pr-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {(referrals || []).length === 0 && (
              <tr><td colSpan={8} className="py-6 text-center text-[#6A5F58] text-sm">No referrals yet.</td></tr>
            )}
            {(referrals || []).map((r) => {
              const pill = r.status === "rewarded"
                ? "bg-[#0F0E0E] text-[#F5EFE6]"
                : r.status === "cancelled"
                  ? "bg-[#B76E79] text-white"
                  : "bg-[#C9A47E] text-[#0F0E0E]";
              return (
                <tr key={`${r.booking_id}-${r.friend_email}`} className="border-b border-[#F0EAD8]" data-testid={`referral-row-${r.booking_id}`}>
                  <td className="py-2 pr-3 text-[#3A322E]">
                    <button
                      type="button"
                      onClick={() => onPeek?.(r.referrer_email)}
                      className="text-left hover:text-[#B76E79] underline decoration-dotted underline-offset-4"
                      title="Open referrer's customer story"
                      data-testid={`peek-referrer-${r.booking_id}`}
                    >
                      {r.referrer_name || r.referrer_email}
                    </button>
                  </td>
                  <td className="py-2 pr-3 text-[#3A322E]">
                    <button
                      type="button"
                      onClick={() => onPeek?.(r.friend_email)}
                      className="text-left hover:text-[#B76E79] underline decoration-dotted underline-offset-4"
                      title="Open friend's customer story"
                      data-testid={`peek-friend-${r.booking_id}`}
                    >
                      {r.friend_email}
                    </button>
                  </td>
                  <td className="py-2 pr-3 text-[#6A5F58]">{r.booking_id}</td>
                  <td className="py-2 pr-3 text-[#3A322E]">£{Number(r.friend_discount || 0).toFixed(2)}</td>
                  <td className="py-2 pr-3">
                    <span className={`px-2 py-0.5 rounded-full text-[10px] uppercase tracking-[0.16em] ${pill}`}>{r.status}</span>
                  </td>
                  <td className="py-2 pr-3 font-mono text-[#0F0E0E]">{r.bonus_voucher_code || "—"}</td>
                  <td className="py-2 pr-3 text-[#6A5F58]">{gbpDate(r.created_at)}</td>
                  <td className="py-2 pr-3 text-right">
                    {r.status === "pending" && (
                      <button
                        type="button"
                        onClick={() => forceReward(r.booking_id)}
                        className="text-xs uppercase tracking-[0.14em] text-[#B76E79] hover:text-[#8f4a52]"
                        data-testid={`referral-force-${r.booking_id}`}
                      >
                        Force reward
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ─────── Root ───────

const AdminRewards = () => {
  const [summary, setSummary] = useState(null);
  const [vouchers, setVouchers] = useState([]);
  const [ledger, setLedger] = useState([]);
  const [referrals, setReferrals] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sweeping, setSweeping] = useState(false);
  const [digesting, setDigesting] = useState(false);
  const [peekEmail, setPeekEmail] = useState(null); // customer story drawer

  const load = async () => {
    setLoading(true);
    try {
      const [s, v, l, r] = await Promise.all([
        axios.get(`${API}/admin/loyalty/summary`),
        axios.get(`${API}/admin/loyalty/vouchers`),
        axios.get(`${API}/admin/loyalty/ledger`),
        axios.get(`${API}/admin/loyalty/referrals`),
      ]);
      setSummary(s.data);
      setVouchers(v.data || []);
      setLedger(l.data || []);
      setReferrals(r.data || []);
    } catch {
      toast.error("Could not load rewards data.");
    } finally {
      setLoading(false);
    }
  };

  const sweepNow = async () => {
    setSweeping(true);
    try {
      const { data } = await axios.post(`${API}/admin/loyalty/sweep-now`);
      const loyalty = data.loyalty || {};
      const refs = data.referrals || {};
      toast.success(
        `Sweep done — vouchers issued: ${loyalty.vouchers_issued || 0} · referrals rewarded: ${refs.rewarded || 0}`,
      );
      load();
    } catch {
      toast.error("Sweep failed.");
    } finally {
      setSweeping(false);
    }
  };

  const sendDigestNow = async () => {
    if (!window.confirm("Send the weekly digest email to the site admin right now?")) return;
    setDigesting(true);
    try {
      const { data } = await axios.post(`${API}/admin/digest/send-now`);
      if (data.sent) toast.success(`Digest emailed to ${data.to}`);
      else toast.error(data.reason || "Could not send.");
    } catch {
      toast.error("Could not send digest.");
    } finally {
      setDigesting(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="space-y-6" data-testid="admin-rewards">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <p className="overline text-[#6A5F58]"><span className="marker-line" /> Rewards</p>
          <h1 className="font-serif text-3xl md:text-4xl text-[#14100F] mt-2">Loyalty & referrals</h1>
        </div>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={load}
            disabled={loading}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-[#0F0E0E] text-[#0F0E0E] hover:bg-[#0F0E0E] hover:text-[#F5EFE6] transition-colors text-xs uppercase tracking-[0.14em] disabled:opacity-60"
            data-testid="rewards-refresh"
          >
            {loading ? <Loader2 size={13} className="animate-spin" /> : <RefreshCw size={13} />}
            Refresh
          </button>
          <button
            type="button"
            onClick={sendDigestNow}
            disabled={digesting}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-[#0F0E0E] text-[#0F0E0E] hover:bg-[#0F0E0E] hover:text-[#F5EFE6] transition-colors text-xs uppercase tracking-[0.14em] disabled:opacity-60"
            data-testid="rewards-send-digest"
            title="Sends the weekly-digest rollup email to the site admin right now"
          >
            {digesting ? <Loader2 size={13} className="animate-spin" /> : <Mail size={13} />}
            Email digest now
          </button>
          <button
            type="button"
            onClick={sweepNow}
            disabled={sweeping}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#0F0E0E] text-[#F5EFE6] hover:bg-[#B76E79] transition-colors text-xs uppercase tracking-[0.14em] disabled:opacity-60"
            data-testid="rewards-sweep-now"
          >
            {sweeping ? <Loader2 size={13} className="animate-spin" /> : <Send size={13} />}
            Run sweep now
          </button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
        <SummaryStat label="Outstanding" value={summary?.outstanding_vouchers} testid="stat-outstanding" hint="Active + reserved" />
        <SummaryStat label="Redeemed" value={summary?.redeemed_vouchers} testid="stat-redeemed" />
        <SummaryStat label="Expiring 30d" value={summary?.expiring_within_30_days} accent testid="stat-expiring" hint="Nudge these guests" />
        <SummaryStat label="Referrals pending" value={summary?.pending_referrals} accent testid="stat-pending-refs" />
        <SummaryStat label="Referrals rewarded" value={summary?.rewarded_referrals} testid="stat-rewarded-refs" />
        <SummaryStat label="Nights this month" value={summary?.nights_credited_this_month} testid="stat-nights-month" hint="Credited via accrual" />
      </div>

      <ManualIssuer onIssued={load} />

      <VouchersTable vouchers={vouchers} onRefresh={load} onPeek={setPeekEmail} />
      <LedgerTable ledger={ledger} onPeek={setPeekEmail} />
      <ReferralsTable referrals={referrals} onRefresh={load} onPeek={setPeekEmail} />

      {peekEmail && <CustomerPeek email={peekEmail} onClose={() => setPeekEmail(null)} />}
    </div>
  );
};

export default AdminRewards;
